import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function PowerCalculator() {
  const [work, setWork] = useState("");
  const [time, setTime] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculatePower = () => {
    const w = parseFloat(work);
    const t = parseFloat(time);

    if (isNaN(w) || isNaN(t) || t === 0) {
      toast.error("Please enter valid values (time must be non-zero)");
      return;
    }

    const power = w / t;
    setResult(power);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setWork("");
    setTime("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Power Calculator" 
      calculatorPath="/calculator/power"
      category="physics"
      subcategory="Energy"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Power Calculator</h1>
          <p className="text-muted-foreground">
            Calculate power from work and time
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: P = W / t</CardTitle>
            <CardDescription>
              Where P is power (W), W is work (J), and t is time (s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="work">Work (J)</Label>
                <Input
                  id="work"
                  type="number"
                  step="0.01"
                  placeholder="Enter work"
                  value={work}
                  onChange={(e) => setWork(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Time (s)</Label>
                <Input
                  id="time"
                  type="number"
                  step="0.01"
                  placeholder="Enter time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculatePower} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} W
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Power (Watts)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["power"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the work done in Joules</p>
                <p>2. Enter the time taken in seconds</p>
                <p>3. Click Calculate to get the power in Watts</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 1000 J of work in 10 s = 100 W power
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
